/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAuthorizationCodeList.ts (Authorization Codes)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Authorization Codes list state and logic.
    Integrates with useDataGrid for pagination, sorting, and search.

────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchAuthorizationCodes } from '../api/authorizationCodesApi';
import { ListAuthorizationCodesParams, AuthorizationCode } from '../types';
import { useContextStore } from '../../../store/context';

/**
 * Column configuration for authorization codes list
 */
export interface AuthorizationCodeColumn {
  key: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  render?: (value: unknown, item: AuthorizationCode) => React.ReactNode;
}

/**
 * Hook for managing authorization codes list
 */
export function useAuthorizationCodeList() {
  const { selectedTenant, selectedDomain } = useContextStore();
  const tenantSlug = selectedTenant?.slug ?? 'main';
  const effectiveDomainId = selectedDomain?.id ?? null;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    if (!effectiveDomainId) {
      return {
        data: [],
        pagination: {
          page: 1,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      };
    }

    const queryParams: ListAuthorizationCodesParams = {
      page: params.page,
      limit: params.limit,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      search: params.search,
      domainId: effectiveDomainId
    };

    const result = await fetchAuthorizationCodes(queryParams, tenantSlug);
    
    return {
      data: result.data,
      pagination: result.pagination
    };
  }, [effectiveDomainId, tenantSlug]);

  const availableColumns: AuthorizationCodeColumn[] = [
    {
      key: 'code',
      label: 'Code',
      visible: true,
      sortable: true
    },
    {
      key: 'alias',
      label: 'Alias',
      visible: true,
      sortable: true
    },
    {
      key: 'description',
      label: 'Description',
      visible: true,
      sortable: false
    },
    {
      key: 'classOfService',
      label: 'Class of Service',
      visible: true,
      sortable: false
    },
    {
      key: 'active',
      label: 'Active',
      visible: true,
      sortable: true
    },
    {
      key: 'createdAt',
      label: 'Created At',
      visible: false,
      sortable: true
    },
    {
      key: 'actions',
      label: 'Actions',
      visible: true,
      sortable: false
    }
  ];

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'code',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'authorization-codes',
    reloadKey: effectiveDomainId ?? null
  });

  return {
    data: data as AuthorizationCode[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
    tenantSlug
  };
}

